import { computed, defineComponent, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useSmartBack } from '@/composables/use-smart-back'
import { Icon as VanIcon, NavBar as VanNavBar } from 'vant'
import { BorderHome } from '@/components/border-home'
import { useWsLiveData } from '@/components/lottery-tabs'
import { GalleryExternalLinks } from '@/components/external-links'
import type { BannerItem } from '@/components/external-links'
import { useRegionStore } from '@/stores/region-store'
import { useVideoStore } from '@/stores/video-store'
import { getApiV1BannersList, getApiV1LotteryDetail49208 } from '@/api/sdk.gen'
import type { Banner, LiveLotteryData } from '@/api/types.gen'

type RecordOverride = Array<{
  number: string
  color: 0 | 1 | 2 | 3
  shengXiao: string
  wuxing: string
}>

function mapBanner(b: Banner): BannerItem {
  return {
    id: String(b.id ?? ''),
    imageUrl: b.images ?? '',
    linkUrl: b.url || undefined,
  }
}

export default defineComponent({
  name: 'VideoDetailPage',
  setup() {
    const route = useRoute()
  const goBack = useSmartBack()
    const regionStore = useRegionStore()
    const videoStore = useVideoStore()

    const period = () => Number(route.params.period)
    const year = () => Number(route.query.year) || new Date().getFullYear()
    const type = () => (route.query.type as string) || 'macau'

    const videoTitle = ref('')
    const videoUrl = ref<string | null>(null)
    const banners = ref<BannerItem[]>([])
    const recordOverride = ref<RecordOverride | null>(null)

    // foot 复用 /live 的 WS 数据,显示最新一期(下次开奖)的信息
    const hkData = ref<LiveLotteryData | null>(null)
    const aocaiData = ref<LiveLotteryData | null>(null)
    const hkDrawingMode = ref(false)
    const aocaiDrawingMode = ref(false)
    const { connectBoth } = useWsLiveData(hkData, aocaiData, hkDrawingMode, aocaiDrawingMode)
    const liveData = computed<LiveLotteryData | null>(() =>
      regionStore.lotteryType === 'hk' ? hkData.value : aocaiData.value,
    )

    async function loadDetail() {
      // id = year*10000 + period*10 + lotteryTypeNum (hk=1, aocai=2)
      const lotteryTypeNum = regionStore.region === 'hk' ? 1 : 2
      const id = year() * 10000 + period() * 10 + lotteryTypeNum
      const res = await getApiV1LotteryDetail49208({
        query: { region: regionStore.region, id },
      })
      const numberList = res.data?.data?.record?.numberList ?? []
      recordOverride.value = numberList.map((n) => ({
        number: n.number ?? '',
        color: (n.color ?? 0) as 0 | 1 | 2 | 3,
        shengXiao: n.shengXiao ?? '',
        wuxing: n.wuXing ?? '',
      }))
    }

    onMounted(async () => {
      const t = type()
      if (t === 'hk' || t === 'macau') regionStore.setLotteryType(t)

      connectBoth()

      if (videoStore.videoList.length === 0 || videoStore.selectedYear !== year()) {
        await videoStore.fetchAll(year())
      }

      const video = videoStore.findByPeriod(period())
      videoTitle.value = video?.videoTitle ?? ''
      videoUrl.value = video?.videoUrl ?? null

      await loadDetail()

      const bannerRes = await getApiV1BannersList({
        query: { page: 1, size: 999, status: 1, type: 4 },
      })
      banners.value = (bannerRes.data?.data?.list ?? []).map(mapBanner)
    })

    // 切换期数/年份/彩种(URL 改变)时重新拉当期开奖
    watch(
      () => [route.params.period, route.query.year, route.query.type],
      () => {
        const t = type()
        if (t === 'hk' || t === 'macau') regionStore.setLotteryType(t)
        loadDetail()
      },
    )

    return () => (
      <div>
        <VanNavBar title={`${videoTitle.value}回放`} fixed placeholder>
          {{
            left: () => (
              <VanIcon name="arrow-left" color="#333" size="16" onClick={() => goBack()} />
            ),
          }}
        </VanNavBar>

        {videoUrl.value && (
          <video
            src={videoUrl.value}
            autoplay
            controls
            muted
            playsinline
            width="100%"
            style="display:block"
          />
        )}

        <BorderHome
          lotteryType={regionStore.lotteryType}
          data={liveData.value}
          recordOverride={recordOverride.value}
          issueOverride={`${year()}${String(period()).padStart(3, '0')}`}
          hideCountdown
        />

        <GalleryExternalLinks banners={banners.value} />
      </div>
    )
  },
})
